import { sha256Hex } from './hash.js';

/**
 * Montagem dos documentos versionados enviados pela interface.
 *
 * A interface usa os MESMOS contratos do aparelho: não existe um formato
 * "de admin" mais permissivo. O transporte administrativo é o único ponto de
 * diferença, e ele NÃO inventa boot.
 */

export function uuid(): string {
  return crypto.randomUUID();
}

export function adminCommand(
  tenantId: string,
  kind: string,
  command: Record<string, unknown>,
  requestId = uuid(),
): Record<string, unknown> {
  return {
    schemaVersion: 'rfid.mvp.admin-command/2.0.0',
    requestId,
    actor: { kind: 'demo_admin', subject: `demo-admin:${tenantId}` },
    occurredAtMs: Date.now(),
    command: { kind, tenantId, ...command },
  };
}

export function operation(
  tenantId: string,
  farmId: string | null,
  operationType: string,
  payload: Record<string, unknown>,
  options: {
    preconditions?: Record<string, unknown>[];
    dependsOn?: string[];
    reasonDetail?: string | null;
    occurredAtMs?: number;
    operationId?: string;
  } = {},
): Record<string, unknown> {
  return {
    schemaVersion: 'rfid.mvp.field-operation/3.0.0',
    operationId: options.operationId ?? uuid(),
    operationType,
    tenantId,
    farmId,
    actor: { kind: 'demo_admin', subject: `demo-admin:${tenantId}` },
    source: { kind: 'demo_admin' },
    occurredAtMs: options.occurredAtMs ?? Date.now(),
    preconditions: options.preconditions ?? [],
    dependsOn: options.dependsOn ?? [],
    reasonDetail: options.reasonDetail ?? null,
    payload,
  };
}

export async function operationBatch(
  tenantId: string,
  operations: Record<string, unknown>[],
): Promise<Record<string, unknown>> {
  const envelope: Record<string, unknown> = {
    schemaVersion: 'rfid.mvp.field-operation-batch/3.0.0',
    batchId: uuid(),
    tenantId,
    actor: { kind: 'demo_admin', subject: `demo-admin:${tenantId}` },
    operationCount: operations.length,
    contentHash: await sha256Hex(canonical(operations)),
    batchFingerprint: '',
    // Transporte administrativo: instante e tentativa, sem boot nem monotônico.
    transport: { kind: 'demo_admin', producedAtMs: Date.now(), attemptNumber: 1 },
    operations,
  };

  envelope.batchFingerprint = await sha256Hex(
    canonical({
      schemaVersion: envelope.schemaVersion,
      batchId: envelope.batchId,
      tenantId: envelope.tenantId,
      actor: envelope.actor,
      operationCount: envelope.operationCount,
      contentHash: envelope.contentHash,
    }),
  );
  return envelope;
}

export const revisionBasis = (aggregateType: string, aggregateId: string, revision: string) => ({
  aggregateType,
  aggregateId,
  basis: { kind: 'revision', revision },
});

/**
 * Forma canônica RFC 8785 do subconjunto que a interface produz.
 *
 * Usa a serialização numérica ECMAScript exigida pela RFC 8785. Coordenadas e
 * precisão são números fracionários legítimos nos documentos de geometria.
 */
export function canonical(value: unknown): string {
  if (value === null) return 'null';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new Error('número não finito no documento');
    if (Number.isInteger(value) && !Number.isSafeInteger(value)) {
      throw new Error('inteiro fora da faixa segura no documento');
    }
    return JSON.stringify(value);
  }
  if (typeof value === 'string') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>).filter(
      ([, item]) => item !== undefined,
    );
    // Ordenação por unidades de código UTF-16, como manda a RFC 8785.
    entries.sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries.map(([key, item]) => `${JSON.stringify(key)}:${canonical(item)}`).join(',')}}`;
  }
  throw new Error('tipo não canonicalizável');
}
